import React, { useState, useEffect } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { useInternalAuth } from '@/hooks/useInternalAuth';
import { photoUploadService } from '@/services/photoUploadService';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Loader2, Save, User, Camera, HelpCircle, Trophy, Star } from 'lucide-react';

const SupportProfileForm: React.FC = () => {
  const { user: supabaseUser, profile } = useAuth();
  const { user: internalUser, profile: internalProfile, refreshProfile } = useInternalAuth();
  const [loading, setLoading] = useState(false);
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);
  const [currentPhotoUrl, setCurrentPhotoUrl] = useState<string | null>(null);
  
  // Use internal profile data for internal users, supabase profile for supabase users
  const activeProfile = internalUser ? internalProfile : profile;
  
  const [formData, setFormData] = useState({
    // Basic Information
    first_name: activeProfile?.first_name || '',
    last_name: activeProfile?.last_name || '',
    username: activeProfile?.username || internalUser?.username || '',
    phone: activeProfile?.phone || '',
    
    // Support specific fields
    support_role: activeProfile?.support_role || '',
    support_experience_level: activeProfile?.support_experience_level || '',
    years_support_experience: activeProfile?.years_support_experience?.toString() || '',
    support_certifications: activeProfile?.support_certifications || '',
    previous_teams_organizations: activeProfile?.previous_teams || '',
    support_availability: activeProfile?.support_availability || [],
    special_skills: activeProfile?.bio || '',
    support_emergency_contact: activeProfile?.support_emergency_contact || '',
    
    // Sports preferences
    preferred_sports: activeProfile?.preferred_sports || [],
    
    // Profile Photo
    profile_photo: null as File | null,
    profile_photo_url: activeProfile?.profile_photo_url || '',
    
    // Privacy settings
    show_profile_public: activeProfile?.show_profile_public || false,
    show_contact_public: activeProfile?.show_contact_public || false,
    show_support_profile_public: activeProfile?.show_support_profile_public || false,
    show_support_contact_public: activeProfile?.show_support_contact_public || false,
  });

  // Update form data when profile loads
  useEffect(() => {
    const loadProfileData = async () => {
      if (activeProfile) {
        setFormData(prev => ({
          ...prev,
          first_name: activeProfile.first_name || '',
          last_name: activeProfile.last_name || '',
          username: activeProfile.username || internalUser?.username || '',
          phone: activeProfile.phone || '',
          support_role: activeProfile.support_role || '',
          support_experience_level: activeProfile.support_experience_level || '',
          years_support_experience: activeProfile.years_support_experience?.toString() || '',
          support_certifications: activeProfile.support_certifications || '',
          previous_teams_organizations: activeProfile.previous_teams || '',
          support_availability: activeProfile.support_availability || [],
          special_skills: activeProfile.bio || '',
          preferred_sports: activeProfile.preferred_sports || [],
          profile_photo_url: activeProfile.profile_photo_url || '',
          show_profile_public: activeProfile.show_profile_public || false,
          show_contact_public: activeProfile.show_contact_public || false,
        }));

        // Load the current profile photo if it exists
        if (activeProfile.profile_photo_url && internalUser) {
          try {
            console.log('Loading existing profile photo:', activeProfile.profile_photo_url);
            
            // For internal users, generate signed URL if needed
            if (!activeProfile.profile_photo_url.startsWith('http')) {
              const signedUrl = await photoUploadService.getProfilePhotoUrl(activeProfile.profile_photo_url);
              console.log('Generated signed URL for existing photo:', signedUrl);
              setCurrentPhotoUrl(signedUrl);
            } else {
              setCurrentPhotoUrl(activeProfile.profile_photo_url);
            }
          } catch (error) {
            console.error('Error loading existing profile photo:', error);
            setCurrentPhotoUrl(activeProfile.profile_photo_url);
          }
        }
      }
    };

    loadProfileData();
  }, [activeProfile, internalUser]);

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handlePhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setFormData(prev => ({ ...prev, profile_photo: file }));
      
      const reader = new FileReader();
      reader.onload = (e) => {
        setPhotoPreview(e.target?.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleArrayChange = (field: string, value: string, checked: boolean) => {
    setFormData(prev => ({
      ...prev,
      [field]: checked 
        ? [...prev[field], value]
        : prev[field].filter((item: string) => item !== value)
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      // First handle photo upload if there's a new photo
      let photoUrl = formData.profile_photo_url;
      
      if (formData.profile_photo && internalUser) {
        console.log('Uploading new profile photo...');
        const uploadResult = await photoUploadService.uploadProfilePhoto(
          formData.profile_photo,
          internalUser.id
        );
        
        if (uploadResult.success && uploadResult.url) {
          photoUrl = uploadResult.url;
          console.log('Photo uploaded successfully:', uploadResult.url);
        } else {
          throw new Error(uploadResult.error || 'Failed to upload photo');
        }
      }

      // Prepare profile data for internal user
      const profileData = {
        user_id: internalUser?.id || '',
        username: formData.username,
        first_name: formData.first_name,
        last_name: formData.last_name,
        phone: formData.phone,
        bio: formData.special_skills,
        profile_photo_url: photoUrl,
        role: internalUser?.role || 'support',
        
        // Support-specific fields
        support_role: formData.support_role,
        support_experience_level: formData.support_experience_level,
        years_support_experience: parseInt(formData.years_support_experience) || null,
        support_certifications: formData.support_certifications,
        previous_teams: formData.previous_teams_organizations,
        support_availability: formData.support_availability,
        
        // Sports preferences
        preferred_sports: formData.preferred_sports,
        
        // Privacy settings
        show_profile_public: formData.show_profile_public,
        show_contact_public: formData.show_contact_public,
      };

      console.log('Saving profile data:', profileData);
      
      // Save profile data using internal profile service
      const { internalProfileService } = await import('@/services/internalProfileService');
      await internalProfileService.upsertProfile(profileData);

      console.log('Profile saved successfully');
      toast.success('Support profile updated successfully!');
      
      // Refresh the profile data in the auth context
      await refreshProfile();
      
    } catch (error) {
      console.error('Error updating profile:', error);
      toast.error(`Failed to update profile: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  const supportRoles = [
    'Score Keeper', 'Timekeeper', 'Equipment Manager', 'Event Coordinator',
    'Registration Assistant', 'Tournament Director', 'Media Coordinator',
    'First Aid Assistant', 'Transportation Coordinator'
  ];

  const experienceLevels = ['Beginner', 'Intermediate', 'Advanced', 'Expert'];

  const availabilityOptions = [
    'Weekday Mornings', 'Weekday Evenings', 'Weekend Mornings', 'Weekend Evenings',
    'Tournament Days', 'Game Days', 'Practice Sessions', 'Special Events'
  ];

  const sportsOptions = [
    'Football', 'Basketball', 'Soccer', 'Baseball', 'Tennis', 
    'Golf', 'Swimming', 'Track & Field', 'Volleyball', 'Hockey'
  ];

  // Check if user is a Score Keeper for special styling
  const isScoreKeeper = formData.support_role === 'Score Keeper';

  return (
    <div className="space-y-6">
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Profile Photo Section */}
        <Card className="bg-gray-800 border-gray-700">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Camera className="h-5 w-5" />
              Profile Photo
              {isScoreKeeper && (
                <Badge className="bg-yellow-500 text-black font-bold flex items-center gap-1">
                  <Trophy className="h-3 w-3" />
                  Score Keeper
                </Badge>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-6">
              <div className={`w-24 h-24 rounded-full flex items-center justify-center overflow-hidden ${
                isScoreKeeper ? 'ring-4 ring-yellow-500 bg-gradient-to-br from-yellow-400 to-yellow-600' : 'bg-gray-700'
              }`}>
                {photoPreview ? (
                  <img 
                    src={photoPreview} 
                    alt="Profile Preview" 
                    className="w-full h-full object-cover"
                  />
                ) : currentPhotoUrl ? (
                  <img 
                    src={currentPhotoUrl} 
                    alt="Current Profile" 
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <User className="h-12 w-12 text-gray-400" />
                )}
              </div>
              <div>
                <Label htmlFor="profile_photo" className="cursor-pointer">
                  <div className={`px-4 py-2 rounded-lg transition-colors inline-flex items-center gap-2 ${
                    isScoreKeeper 
                      ? 'bg-yellow-500 text-black hover:bg-yellow-600 font-bold' 
                      : 'bg-sports-green text-black hover:bg-green-600'
                  }`}>
                    <Camera className="h-4 w-4" />
                    {currentPhotoUrl || photoPreview ? 'Change Photo' : 'Upload Photo'}
                  </div>
                </Label>
                <Input
                  id="profile_photo"
                  type="file"
                  accept="image/*"
                  onChange={handlePhotoChange}
                  className="hidden"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Basic Information */}
        <Card className="bg-gray-800 border-gray-700">
          <CardHeader>
            <CardTitle className="text-white">Basic Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="first_name" className="text-gray-300">First Name *</Label>
                <Input
                  id="first_name"
                  value={formData.first_name}
                  onChange={(e) => handleInputChange('first_name', e.target.value)}
                  className="bg-gray-700 border-gray-600 text-white"
                  required
                />
              </div>
              <div>
                <Label htmlFor="last_name" className="text-gray-300">Last Name *</Label>
                <Input
                  id="last_name"
                  value={formData.last_name}
                  onChange={(e) => handleInputChange('last_name', e.target.value)}
                  className="bg-gray-700 border-gray-600 text-white"
                  required
                />
              </div>
              <div>
                <Label htmlFor="username" className="text-gray-300">Username *</Label>
                <Input
                  id="username"
                  value={formData.username}
                  onChange={(e) => handleInputChange('username', e.target.value)}
                  className="bg-gray-700 border-gray-600 text-white"
                  required
                />
              </div>
              <div>
                <Label htmlFor="phone" className="text-gray-300">Phone Number</Label>
                <Input
                  id="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => handleInputChange('phone', e.target.value)}
                  className="bg-gray-700 border-gray-600 text-white"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Support Role Information */}
        <Card className={`border-gray-700 ${
          isScoreKeeper 
            ? 'bg-gradient-to-br from-gray-800 to-yellow-900/20 border-yellow-500/50' 
            : 'bg-gray-800'
        }`}>
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <HelpCircle className="h-5 w-5" />
              Support Role Information
              {isScoreKeeper && (
                <div className="flex items-center gap-2">
                  <Badge className="bg-yellow-500 text-black font-bold flex items-center gap-1">
                    <Star className="h-3 w-3" />
                    Elite Score Keeper
                  </Badge>
                </div>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="support_role" className="text-gray-300">Primary Support Role</Label>
                <Select 
                  value={formData.support_role} 
                  onValueChange={(value) => handleInputChange('support_role', value)}
                >
                  <SelectTrigger className={`border-gray-600 text-white ${
                    isScoreKeeper 
                      ? 'bg-yellow-900/20 border-yellow-500/50' 
                      : 'bg-gray-700'
                  }`}>
                    <SelectValue placeholder="Select your primary role" />
                  </SelectTrigger>
                  <SelectContent className="bg-gray-700 border-gray-600">
                    {supportRoles.map((role) => (
                      <SelectItem key={role} value={role} className={
                        role === 'Score Keeper' ? 'bg-yellow-500/10 text-yellow-300 font-medium' : ''
                      }>
                        {role === 'Score Keeper' && <Trophy className="h-4 w-4 inline mr-2" />}
                        {role}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {isScoreKeeper && (
                  <p className="text-yellow-400 text-sm mt-2 flex items-center gap-1">
                    <Star className="h-4 w-4" />
                    Score Keepers are vital to the sports ecosystem!
                  </p>
                )}
              </div>
              
              <div>
                <Label htmlFor="support_experience_level" className="text-gray-300">Experience Level</Label>
                <Select 
                  value={formData.support_experience_level} 
                  onValueChange={(value) => handleInputChange('support_experience_level', value)}
                >
                  <SelectTrigger className="bg-gray-700 border-gray-600 text-white">
                    <SelectValue placeholder="Select experience level" />
                  </SelectTrigger>
                  <SelectContent className="bg-gray-700 border-gray-600">
                    {experienceLevels.map((level) => (
                      <SelectItem key={level} value={level}>{level}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label htmlFor="years_support_experience" className="text-gray-300">Years of Experience</Label>
                <Input
                  id="years_support_experience"
                  type="number"
                  value={formData.years_support_experience}
                  onChange={(e) => handleInputChange('years_support_experience', e.target.value)}
                  className="bg-gray-700 border-gray-600 text-white"
                />
              </div>
            </div>
            
            <div>
              <Label htmlFor="support_certifications" className="text-gray-300">Certifications</Label>
              <Textarea
                id="support_certifications"
                value={formData.support_certifications}
                onChange={(e) => handleInputChange('support_certifications', e.target.value)}
                placeholder="List any relevant certifications..."
                className="bg-gray-700 border-gray-600 text-white"
              />
            </div>
            
            <div>
              <Label htmlFor="previous_teams_organizations" className="text-gray-300">Previous Teams/Organizations</Label>
              <Textarea
                id="previous_teams_organizations"
                value={formData.previous_teams_organizations}
                onChange={(e) => handleInputChange('previous_teams_organizations', e.target.value)}
                placeholder="List organizations you've supported before..."
                className="bg-gray-700 border-gray-600 text-white"
              />
            </div>
            
            <div>
              <Label htmlFor="special_skills" className="text-gray-300">Special Skills</Label>
              <Textarea
                id="special_skills"
                value={formData.special_skills}
                onChange={(e) => handleInputChange('special_skills', e.target.value)}
                placeholder="Any special skills or abilities..."
                className="bg-gray-700 border-gray-600 text-white"
              />
            </div>
          </CardContent>
        </Card>

        {/* Sports Preferences */}
        <Card className="bg-gray-800 border-gray-700">
          <CardHeader>
            <CardTitle className="text-white">Sports Preferences</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {sportsOptions.map((sport) => (
                <div key={sport} className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id={sport}
                    checked={formData.preferred_sports.includes(sport)}
                    onChange={(e) => handleArrayChange('preferred_sports', sport, e.target.checked)}
                    className="w-4 h-4 text-sports-green bg-gray-700 border-gray-600 rounded focus:ring-sports-green"
                  />
                  <Label htmlFor={sport} className="text-gray-300 text-sm cursor-pointer">
                    {sport}
                  </Label>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Availability */}
        <Card className="bg-gray-800 border-gray-700">
          <CardHeader>
            <CardTitle className="text-white">Availability</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {availabilityOptions.map((time) => (
                <div key={time} className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id={time}
                    checked={formData.support_availability.includes(time)}
                    onChange={(e) => handleArrayChange('support_availability', time, e.target.checked)}
                    className="w-4 h-4 text-sports-green bg-gray-700 border-gray-600 rounded focus:ring-sports-green"
                  />
                  <Label htmlFor={time} className="text-gray-300 text-sm cursor-pointer">
                    {time}
                  </Label>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Emergency Contact */}
        <Card className="bg-gray-800 border-gray-700">
          <CardHeader>
            <CardTitle className="text-white">Emergency Contact</CardTitle>
          </CardHeader>
          <CardContent>
            <div>
              <Label htmlFor="support_emergency_contact" className="text-gray-300">Emergency Contact Information</Label>
              <Textarea
                id="support_emergency_contact"
                value={formData.support_emergency_contact}
                onChange={(e) => handleInputChange('support_emergency_contact', e.target.value)}
                placeholder="Name and phone number of emergency contact..."
                className="bg-gray-700 border-gray-600 text-white"
              />
            </div>
          </CardContent>
        </Card>

        {/* Privacy Settings */}
        <Card className="bg-gray-800 border-gray-700">
          <CardHeader>
            <CardTitle className="text-white">Privacy Settings</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between p-4 bg-gray-700 rounded-lg">
              <div>
                <Label className="text-white font-medium">Make Profile Public</Label>
                <p className="text-sm text-gray-400">Allow others to view your profile</p>
              </div>
              <Switch
                checked={formData.show_profile_public}
                onCheckedChange={(checked) => handleInputChange('show_profile_public', checked)}
              />
            </div>
            
            <div className="flex items-center justify-between p-4 bg-gray-700 rounded-lg">
              <div>
                <Label className="text-white font-medium">Show Contact Information</Label>
                <p className="text-sm text-gray-400">Display contact info publicly</p>
              </div>
              <Switch
                checked={formData.show_contact_public}
                onCheckedChange={(checked) => handleInputChange('show_contact_public', checked)}
              />
            </div>
            
            <div className="flex items-center justify-between p-4 bg-gray-700 rounded-lg">
              <div>
                <Label className="text-white font-medium">Show Support Profile</Label>
                <p className="text-sm text-gray-400">Display support role details publicly</p>
              </div>
              <Switch
                checked={formData.show_support_profile_public}
                onCheckedChange={(checked) => handleInputChange('show_support_profile_public', checked)}
              />
            </div>
            
            <div className="flex items-center justify-between p-4 bg-gray-700 rounded-lg">
              <div>
                <Label className="text-white font-medium">Show Support Contact</Label>
                <p className="text-sm text-gray-400">Display support contact info publicly</p>
              </div>
              <Switch
                checked={formData.show_support_contact_public}
                onCheckedChange={(checked) => handleInputChange('show_support_contact_public', checked)}
              />
            </div>
          </CardContent>
        </Card>

        {/* Submit Button */}
        <div className="flex justify-end">
          <Button
            type="submit"
            disabled={loading}
            className={`text-black font-bold ${
              isScoreKeeper 
                ? 'bg-yellow-500 hover:bg-yellow-600' 
                : 'bg-sports-green hover:bg-green-600'
            }`}
          >
            {loading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="mr-2 h-4 w-4" />
                Save Support Profile
                {isScoreKeeper && <Trophy className="ml-2 h-4 w-4" />}
              </>
            )}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default SupportProfileForm;
